import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import TopWedge from "@/components/TopWedge";
import RightDrawer from "@/components/RightDrawer";
import { C, PROGRAM_META, colorSchemeForProgram } from "@/lib/brand";
import useMarketingPrograms from "@/lib/useMarketingPrograms";
import WhatsNextTile from "@/components/WhatsNextTile";

export default function Landing() {
  const navigate = useNavigate();
  const marketingPrograms = useMarketingPrograms();
  // The currently-previewed program (full panel object, not just slug)
  // so the drawer can render without re-fetching. Closed = null.
  const [previewProgram, setPreviewProgram] = useState(null);

  // Compose program panels. The BE is now authoritative — when the
  // marketing API list has loaded, we trust it: respect `isVisibleOnLanding`
  // and DON'T inject PROGRAM_META fallback tiles to fill gaps (that would
  // resurrect intentionally-hidden programs like Skin & Beauty as fake
  // "coming soon" tiles). PROGRAM_META fallbacks only run during the
  // initial loading state so the grid isn't empty on first paint.
  const visiblePrograms = (marketingPrograms || []).filter((p) => p?.isVisibleOnLanding !== false);
  const liveSlugs = new Set(visiblePrograms.map((p) => (p.slug || "").toLowerCase()));
  const isInitialLoad = marketingPrograms === null;
  const fallbackTiles = isInitialLoad
    ? PROGRAM_META
        .filter((m) => !m.slugMatch.some((x) => Array.from(liveSlugs).some((s) => s.includes(x))))
        .filter((m) => !m.slugMatch.some((x) => x === "sexual-wellness" || x === "sexual"))
        .map((m) => ({
          slug: m.slugMatch[0],
          name: `${m.t}${m.t2 ? " " + m.t2 : ""}`,
          tagline: m.q,
          categoryNumber: m.num,
          status: "coming_soon",
          color: m.color,
          accentColor: m.dark,
          darkColor: m.dark,
          _isFallback: true,
        }))
    : [];
  const panels = [...visiblePrograms, ...fallbackTiles];
  // Real program count drives the headline copy and the placeholder's
  // span behaviour. `visibleCount % 2 === 0` → placeholder spans both
  // columns so the grid finishes flush; otherwise placeholder is a
  // normal single-width tile.
  const visibleCount = panels.length;

  return (
    <div data-testid="landing-page" style={{ background: C.cream }}>
      <PublicNav variant="dark" />

      {/* HERO */}
      <section
        data-testid="hero"
        style={{
          background: C.ink,
          color: C.cream,
          padding: "112px 24px 140px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circles */}
        <div
          aria-hidden
          className="hero-orb-tl"
          style={{
            position: "absolute",
            borderRadius: "50%",
            background: C.peach,
            opacity: 0.08,
            filter: "blur(2px)",
            pointerEvents: "none",
          }}
        />
        {/* Bottom-right orb — sized/positioned so it never kisses the diagonal wedge
            below. On mobile it's smaller and raised; on md+ it scales back up. */}
        <div
          aria-hidden
          className="hero-orb-br"
          style={{
            position: "absolute",
            borderRadius: "50%",
            background: C.sage,
            opacity: 0.08,
            pointerEvents: "none",
          }}
        />

        <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 2 }}>
          <Eyebrow color={C.peach} testId="hero-eyebrow">★ EMPLOYER-SPONSORED CARE</Eyebrow>
          <h1
            style={{
              margin: "28px 0 0",
              fontSize: "clamp(48px, 9vw, 110px)",
              fontWeight: 800,
              lineHeight: 0.92,
              letterSpacing: "-0.04em",
              color: C.cream,
              textTransform: "uppercase",
            }}
          >
            <span style={{ fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontWeight: 400, textTransform: "none", color: C.peach }}>Wellness</span>
            <br />
            AS AN
            <br />
            <span style={{ color: C.peach }}>EMPLOYEE BENEFIT.</span>
          </h1>
          <p style={{ marginTop: 36, maxWidth: 560, fontSize: 17, lineHeight: 1.55, color: "rgba(250,246,240,0.75)" }}>
            VOYA9 for Employers is a physician-supervised wellness platform delivered as a benefit. Insurance-eligible compound therapy, licensed clinician oversight, and personalized programs — at scale.
          </p>
          <div style={{ marginTop: 44 }}>
            <Button variant="peach" size="lg" testId="hero-cta" onClick={() => window.location.assign("mailto:enterprise@voya9.com?subject=VOYA9%20for%20Employers%20%E2%80%94%20request%20a%20demo")}>
              REQUEST A DEMO →
            </Button>
            <div style={{ marginTop: 16, fontSize: 12, color: "rgba(250,246,240,0.55)", letterSpacing: "0.05em" }}>
              APT-code billing supported · Insurance claims handled
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="programs" data-testid="programs-section" style={{ background: C.cream, padding: "108px 0 96px", position: "relative" }}>
        <TopWedge color={C.ink} dir="right" />
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px" }}>
          <Eyebrow testId="programs-eyebrow">01 / WHAT WE WORK ON</Eyebrow>
          <Headline before={`${areaCountWord(panels.length)} AREAS.`} italic="endless impact." size={72} className="mt-6" />
        </div>

        {/*
          Full-bleed tile grid — escapes the section's outer 24px padding
          intentionally so every program tile spans edge-to-edge of the
          viewport on mobile. Previously the parent's `padding: "0 24px"`
          left a thin cream gutter on the right side of each tile (the
          "inner div" the user spotted in the iPhone screenshots). On
          desktop the inner max-width container above (constrained to
          1100) keeps the heading aligned with the rest of the marketing
          page; the grid happily extends past it.
        */}
        <div style={{ marginTop: 56, display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 0, maxWidth: 1100, marginLeft: "auto", marginRight: "auto" }} className="!grid-cols-1 md:!grid-cols-2">
          {panels.map((p, idx) => {
              const scheme = colorSchemeForProgram(p);
              const num = p.categoryNumber || String(idx + 1).padStart(2, "0");
              const isAvailable = (p.status || "").toLowerCase() === "available";
              const target = `/programs/${p.slug}`;
              const headline = p.name || p.heroName || p.title || "";
              const tagline = p.tagline || p.shortDescription || "";
              const canPreview = !p._isFallback; // fallback tiles have no BE body to preview
              return (
                <div
                  key={p.slug || num}
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate(target)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(target);
                    }
                  }}
                  data-testid={`program-panel-${p.slug || num}`}
                  style={{
                    background: scheme.background,
                    color: scheme.textPrimary,
                    padding: "56px 40px 64px",
                    textAlign: "left",
                    display: "block",
                    cursor: "pointer",
                    minHeight: 280,
                    position: "relative",
                    // Numeral watermark contained to this card's bounds.
                    overflow: "hidden",
                    transition: "transform 200ms ease",
                    textDecoration: "none",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                >
                  <span
                    aria-hidden
                    style={{
                      position: "absolute",
                      top: 24,
                      right: 20,
                      zIndex: 1,
                      fontFamily: '"Instrument Serif", Georgia, serif',
                      fontStyle: "italic",
                      fontSize: "clamp(48px, 7vw, 78px)",
                      lineHeight: 1,
                      opacity: 0.5,
                      color: scheme.numberOverlay || scheme.textSecondary,
                      pointerEvents: "none",
                      letterSpacing: "-0.04em",
                      // Ink buffer for italic Instrument Serif's rightward
                      // flourish (right curl on 2/3/5) so overflow: hidden
                      // on the card doesn't clip the glyph edge on mobile.
                      paddingRight: 8,
                    }}
                  >
                    {num}
                  </span>
                  <div style={{ position: "relative", zIndex: 2 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: scheme.textSecondary, opacity: 0.85 }}>
                      CATEGORY {num}
                    </div>
                    <div style={{ marginTop: 22, fontSize: 38, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1, color: scheme.textPrimary, textTransform: "uppercase", maxWidth: "84%" }}>
                      {headline}
                    </div>
                    {tagline && (
                      <div style={{ marginTop: 16, fontFamily: '"Instrument Serif", serif', fontStyle: "italic", fontSize: 22, color: scheme.textSecondary }}>
                        "{tagline}"
                      </div>
                    )}
                    <div style={{ marginTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: scheme.textSecondary }}>
                        {isAvailable ? "AVAILABLE NOW →" : "LEARN MORE →"}
                      </span>
                      {canPreview && (
                        <button
                          type="button"
                          // Stop the click from bubbling to the tile's
                          // navigate handler — preview means "stay here".
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewProgram(p);
                          }}
                          data-testid={`program-panel-${p.slug}-preview`}
                          style={{
                            background: "transparent",
                            border: `1px solid ${scheme.textSecondary}`,
                            color: scheme.textSecondary,
                            padding: "8px 14px",
                            fontSize: 10,
                            fontWeight: 800,
                            letterSpacing: "0.2em",
                            cursor: "pointer",
                            fontFamily: "inherit",
                          }}
                        >
                          PREVIEW
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          {/* "What's next" placeholder — always closes out the program
              grid in the Skin & Beauty mustard accent. Auto-balances:
              spans full width on desktop when the visible program count
              is even (so the grid renders an even number of cells); sits
              as a single tile when odd. Shared component used across
              Landing, in-app Browse, and PreSelectionHome. */}
          <WhatsNextTile forceWide={visibleCount % 2 === 0} />
        </div>
        {/* Subtle "Not sure where to start?" cross-link — routes
            to the concerns catalog. Sits between the program grid
            and the rest of the marketing flow so it catches
            uncertain visitors without competing with primary CTAs. */}
        <div style={{ maxWidth: 1100, margin: "40px auto 0", padding: "0 24px", textAlign: "center" }}>
          <Link
            to="/what-we-help-with"
            data-testid="landing-what-we-help-with-link"
            style={{
              display: "inline-block",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.16em",
              color: C.ink,
              textDecoration: "none",
              borderBottom: `1.5px solid ${C.ink}`,
              paddingBottom: 4,
              textTransform: "uppercase",
            }}
          >
            Not sure where to start? See what we help with →
          </Link>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" data-testid="how-section" style={{ background: C.ink, color: C.cream, padding: "128px 24px 120px", position: "relative" }}>
        <TopWedge color={C.cream} dir="left" />
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Eyebrow color={C.peach} testId="how-eyebrow">02 / THE PROCESS</Eyebrow>
          <Headline before="HOW IT" italic="actually works." color={C.cream} italicColor={C.peach} size={72} className="mt-6" />

          <div style={{ marginTop: 64, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 32 }} className="!grid-cols-1 md:!grid-cols-3">
            {[
              { n: "01", t: "HR ENROLLS THE TEAM", s: "Simple integration with your existing benefits stack. Onboarding materials, comms templates, HRIS-friendly rosters." },
              { n: "02", t: "EMPLOYEES ASSESS PRIVATELY", s: "Each employee's health data stays confidential. Employers never see individual submissions — only aggregate engagement metrics." },
              { n: "03", t: "INSURANCE HANDLES BILLING", s: "APT-coded claims filed directly. Physician & nurse consultations, monitoring, and shipping bundled per member." },
            ].map((s) => (
              <div key={s.n} data-testid={`how-step-${s.n}`} style={{ paddingTop: 12 }}>
                <div style={{ fontFamily: '"Instrument Serif", serif', fontStyle: "italic", fontSize: 88, color: C.peach, lineHeight: 0.9 }}>
                  {s.n}
                </div>
                <div style={{ marginTop: 12, fontSize: 15, fontWeight: 800, letterSpacing: "0.18em", color: C.cream }}>
                  {s.t}
                </div>
                <div style={{ marginTop: 14, fontSize: 15, lineHeight: 1.6, color: "rgba(250,246,240,0.7)" }}>
                  {s.s}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" style={{ background: C.cream, padding: "132px 24px 120px", position: "relative" }}>
        <TopWedge color={C.ink} dir="right" />
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Eyebrow testId="testimonials-eyebrow">03 / FROM OUR MEMBERS</Eyebrow>
          <h2
            style={{
              margin: "28px 0 0",
              fontSize: "clamp(40px, 7vw, 80px)",
              fontFamily: '"Instrument Serif", serif',
              fontStyle: "italic",
              fontWeight: 400,
              color: C.ink,
              letterSpacing: "-0.02em",
              lineHeight: 1,
            }}
          >
            "It just made sense."
          </h2>
          <div style={{ marginTop: 56, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 40 }} className="!grid-cols-1 md:!grid-cols-3">
            {[
              { q: "I'd tried everything. Having an actual physician guide my plan made all the difference.", n: "JORDAN K.", l: "MEMBER · 8 MO" },
              { q: "The check-ins keep me on track. It feels like having a coach in my pocket.", n: "ALEX M.", l: "MEMBER · 6 MO" },
              { q: "What surprised me was how thoughtful the whole experience is. From the questions to the packaging.", n: "SAM T.", l: "MEMBER · 4 MO" },
            ].map((t, i) => (
              <div key={i} data-testid={`testimonial-${i}`}>
                <div style={{ fontFamily: '"Instrument Serif", serif', fontStyle: "italic", fontSize: 64, color: C.peachDeep, lineHeight: 0.6 }}>"</div>
                <div style={{ marginTop: 12, fontSize: 17, lineHeight: 1.55, color: C.ink }}>{t.q}</div>
                <div style={{ marginTop: 24, fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.ink }}>{t.n}</div>
                <div style={{ marginTop: 4, fontSize: 11, color: C.warmGray, letterSpacing: "0.15em" }}>{t.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY MEMBERS */}
      <section data-testid="why-section" style={{ background: C.peach, padding: "132px 24px 120px", position: "relative" }}>
        <TopWedge color={C.cream} dir="left" />
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64 }} className="!grid-cols-1 md:!grid-cols-2">
          <div>
            <Eyebrow color={C.peachDark} testId="why-eyebrow">04 / MEMBERSHIP</Eyebrow>
            <Headline before="WHY VOYA9" italic="matters." color={C.peachDark} italicColor={C.peachDark} size={72} className="mt-6" />
          </div>
          <div>
            {[
              "Real licensed physicians overseeing every employee — not symptom-checker bots",
              "Individually personalized programs delivered at company scale",
              "Monthly clinician check-ins to keep engagement high",
              "Discreet packaging, predictable delivery — nothing crosses HR",
              "Every batch independently lab-tested and physician-verified",
              "Insurance-eligible with APT-coded billing, no reimbursement paperwork",
            ].map((t, i) => (
              <div key={i} data-testid={`why-item-${i}`} style={{ display: "flex", gap: 24, paddingTop: 22, paddingBottom: 22, borderBottom: `1px solid ${C.peachDark}33` }}>
                <span style={{ fontFamily: '"Instrument Serif", serif', fontStyle: "italic", fontSize: 28, color: C.peachDark, minWidth: 40 }}>0{i + 1}</span>
                <span style={{ fontSize: 17, color: C.peachDark, lineHeight: 1.4 }}>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="final-cta" style={{ background: C.sage, padding: "152px 24px 140px", textAlign: "center", position: "relative" }}>
        <TopWedge color={C.peach} dir="right" />
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <Eyebrow color={C.sageDark} testId="final-eyebrow">★ READY?</Eyebrow>
          <Headline before="OFFER IT AS A" italic="benefit." color={C.sageDark} italicColor={C.sageDark} size={88} className="mt-6" />
          <p style={{ marginTop: 28, fontSize: 17, color: C.sageDark, lineHeight: 1.55 }}>
            Bring physician-supervised wellness to your team.<br />
            Insurance-eligible. Delivered discreetly. Managed for you.
          </p>
          <div style={{ marginTop: 44 }}>
            <Button
              variant="primary"
              size="lg"
              testId="final-cta-btn"
              onClick={() => window.location.assign("mailto:enterprise@voya9.com?subject=VOYA9%20for%20Employers%20%E2%80%94%20request%20a%20demo")}
              style={{ color: C.sage }}
            >
              REQUEST A DEMO →
            </Button>
          </div>
        </div>
      </section>

      <Footer prevColor={C.sage} wedgeDir="left" />

      <RightDrawer
        open={!!previewProgram}
        onClose={() => setPreviewProgram(null)}
        eyebrow={previewProgram ? `★ CATEGORY ${previewProgram.categoryNumber || ""}` : "★ PROGRAM PREVIEW"}
        title={previewProgram?.name || previewProgram?.heroName || previewProgram?.title || "Program"}
        testId="program-preview-drawer"
      >
        {previewProgram && (
          <ProgramPreviewContent program={previewProgram} onOpenFull={() => navigate(`/programs/${previewProgram.slug}`)} />
        )}
      </RightDrawer>
    </div>
  );
}

// Drawer body for the program-tile preview. Pulls all the easy stuff
// straight off the marketing payload — taglines, summaries, "what
// members get" bullets, status. Goal: enough information to decide
// "I want to read more" without leaving the page.
function ProgramPreviewContent({ program, onOpenFull }) {
  const Section = ({ heading, children }) => (
    <section style={{ marginBottom: 28 }}>
      <h3 style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.16em", color: C.peachDeep, marginBottom: 10 }}>
        ★ {heading.toUpperCase()}
      </h3>
      <div style={{ fontSize: 14.5, lineHeight: 1.65, color: C.ink }}>{children}</div>
    </section>
  );
  const summary =
    program.summary ||
    program.subtitle ||
    program.tagline ||
    program.shortDescription ||
    program.description;
  const bullets =
    (Array.isArray(program.highlights) && program.highlights) ||
    (Array.isArray(program.benefits) && program.benefits) ||
    (Array.isArray(program.bullets) && program.bullets) ||
    [];
  const isAvailable = (program.status || "").toLowerCase() === "available";
  return (
    <div>
      <div
        style={{
          display: "inline-block",
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: "0.22em",
          padding: "5px 10px",
          background: isAvailable ? C.sage : C.peach,
          color: isAvailable ? C.ink : C.peachDark,
          marginBottom: 18,
        }}
      >
        {isAvailable ? "AVAILABLE NOW" : "COMING SOON"}
      </div>
      {summary && (
        <Section heading="The pitch">
          {summary}
        </Section>
      )}
      {bullets.length > 0 && (
        <Section heading="What members get">
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            {bullets.slice(0, 6).map((b, i) => (
              <li key={i} style={{ marginBottom: 6 }}>
                {typeof b === "string" ? b : (b?.text || b?.label || "")}
              </li>
            ))}
          </ul>
        </Section>
      )}
      <Section heading="How it works">
        Pick this program, complete a brief assessment, and your physician designs a personalized protocol. Compounds and lifestyle guidance ship together — discreet packaging, predictable cadence. Pause or cancel anytime.
      </Section>
      <div style={{ marginTop: 32, display: "flex", gap: 12, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={onOpenFull}
          data-testid="program-preview-open-full"
          style={{
            background: C.ink,
            color: C.cream,
            border: 0,
            padding: "14px 22px",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.2em",
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          OPEN FULL PROGRAM →
        </button>
      </div>
    </div>
  );
}


// Map a count to its uppercase word (1..10). Used by the "N AREAS.
// endless impact." headline so the marketing copy stays in lockstep
// with however many programs the admin has actually marked as
// "ON LANDING". Falls back to a digit when out of range.
function areaCountWord(n) {
  const WORDS = ["ZERO", "ONE", "TWO", "THREE", "FOUR", "FIVE", "SIX", "SEVEN", "EIGHT", "NINE", "TEN"];
  return WORDS[n] || String(n);
}
